import { create } from "zustand";

/** Body of the structured 409 the backend emits when a non-walk-in
 *  reservation would exceed the kitchen-capacity ceiling.
 *
 *  Mirrors ``backend/apps/reservations/services/validation.py::
 *  kitchen_capacity_override_body``. The agent / staff client uses these
 *  fields to render the confirmation prompt and decide whether to retry
 *  with ``acknowledge_kitchen_capacity_override = true``. */
export interface KitchenCapacityContext {
  cap: number;
  current_pax: number;
  requested_pax: number;
  would_be: number;
  slot_local_time: string;
  slot_local_date: string;
}

interface PendingOverride {
  context: KitchenCapacityContext;
  resolve: (acknowledge: boolean) => void;
}

interface State {
  pending: PendingOverride | null;
  /** Open the dialog and resolve with the user's choice. */
  open: (context: KitchenCapacityContext) => Promise<boolean>;
  /** Programmatic close (used by the dialog's buttons). */
  close: (acknowledge: boolean) => void;
}

// Standalone, non-persisted store — the pending override is request-scoped
// and must NOT survive a reload. Lives outside the main ``useAppStore`` so
// the persist middleware never touches it.
export const useKitchenCapOverrideStore = create<State>((set, get) => ({
  pending: null,
  open: (context) =>
    new Promise<boolean>((resolve) => {
      set({ pending: { context, resolve } });
    }),
  close: (acknowledge) => {
    const { pending } = get();
    if (pending) {
      pending.resolve(acknowledge);
      set({ pending: null });
    }
  },
}));
